import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import * as api from '../api/store.js';
import { readStoredValue, writeStoredValue } from '../lib/storage.js';

const BasketContext = createContext(null);
const SAVED_USER_KEY = 'skyexpress_user';

const emptyBasket = {
  ident: null,
  orderId: null,
  items: [],
  count: 0,
  basePrice: 0,
  salesTax: 0,
  totalPrice: 0,
  currency: 'EUR',
  coupons: [],
  requiresAuthorization: false,
  authOptions: [],
  checkoutUrl: null,
};

const initialState = {
  isReady: false,
  isMutating: false,
  loggedIn: false,
  username: null,
  csrfToken: '',
  handoff: null,
  rankState: 'logged_out',
  ...emptyBasket,
};

function basketState(payload) {
  const basket = payload?.basket ?? {};

  return {
    ident: basket.ident ?? null,
    orderId: basket.order_id ?? null,
    items: Array.isArray(basket.packages) ? basket.packages : [],
    count: Number(payload?.count ?? 0),
    basePrice: Number(basket.base_price ?? 0),
    salesTax: Number(basket.sales_tax ?? 0),
    totalPrice: Number(basket.total_price ?? 0),
    currency: String(basket.currency || 'EUR').toUpperCase(),
    coupons: Array.isArray(basket.coupons) ? basket.coupons : [],
    requiresAuthorization: Boolean(basket.requires_authorization),
    authOptions: Array.isArray(basket.auth_options) ? basket.auth_options : [],
    checkoutUrl: basket.checkout_url ?? null,
  };
}

function basketReducer(state, action) {
  switch (action.type) {
    case 'session':
      return {
        ...state,
        isReady: true,
        loggedIn: Boolean(action.payload.logged_in),
        username: action.payload.username ?? null,
        csrfToken: action.payload.csrf_token || state.csrfToken,
        count: Number(action.payload.basket_count ?? state.count),
        handoff: action.payload.handoff ?? state.handoff,
        rankState: action.payload.rank_state ?? state.rankState,
      };
    case 'basket':
      return {
        ...state,
        ...basketState(action.payload),
        isReady: true,
        csrfToken: action.payload.csrf_token || state.csrfToken,
      };
    case 'mutating':
      return { ...state, isMutating: action.payload };
    case 'token':
      return action.payload ? { ...state, csrfToken: action.payload } : state;
    case 'handoffSeen':
      return { ...state, handoff: null };
    case 'loggedOut':
      return { ...initialState, isReady: true, csrfToken: action.payload || state.csrfToken };
    default:
      return state;
  }
}

export function BasketProvider({ children }) {
  const [state, dispatch] = useReducer(basketReducer, initialState);
  const stateRef = useRef(state);
  stateRef.current = state;

  const refresh = useCallback(async () => {
    const data = await api.fetchBasket();
    if (data.success) dispatch({ type: 'basket', payload: data });
    return data;
  }, []);

  const withCsrf = useCallback(async (action) => {
    let token = stateRef.current.csrfToken;
    if (!token) {
      const session = await api.checkSession();
      token = session.csrf_token || '';
      dispatch({ type: 'session', payload: session });
    }

    const result = await action(token);
    dispatch({ type: 'token', payload: result?.csrf_token });
    return result;
  }, []);

  const mutate = useCallback(async (action) => {
    if (stateRef.current.isMutating) {
      return { success: false, retry: true, error: 'Your basket is already being updated.' };
    }

    dispatch({ type: 'mutating', payload: true });
    try {
      const result = await withCsrf(action);
      if (result.success && result.basket) {
        dispatch({ type: 'basket', payload: result });
      } else if (result.retry) {
        await refresh();
      }
      return result;
    } finally {
      dispatch({ type: 'mutating', payload: false });
    }
  }, [refresh, withCsrf]);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      const session = await api.checkSession();
      if (cancelled) return;

      if (session.logged_in && session.username) {
        writeStoredValue(SAVED_USER_KEY, session.username);
        dispatch({ type: 'session', payload: session });
        await refresh();
        return;
      }

      const savedUser = readStoredValue(SAVED_USER_KEY);
      if (!savedUser) {
        dispatch({ type: 'session', payload: session });
        return;
      }

      const result = await api.login(savedUser);
      if (cancelled) return;
      if (result.success) {
        dispatch({ type: 'session', payload: { ...result, logged_in: true, username: savedUser } });
        await refresh();
      } else {
        writeStoredValue(SAVED_USER_KEY, null);
        dispatch({ type: 'loggedOut' });
      }
    }

    bootstrap();
    return () => { cancelled = true; };
  }, [refresh]);

  const login = useCallback(async (username) => {
    const result = await api.login(username);
    if (!result.success) return result;

    writeStoredValue(SAVED_USER_KEY, username);
    dispatch({ type: 'session', payload: { ...result, logged_in: true, username } });
    await refresh();
    return result;
  }, [refresh]);

  const logout = useCallback(async () => {
    const result = await withCsrf((token) => api.logout(token));
    if (result.success) {
      writeStoredValue(SAVED_USER_KEY, null);
      dispatch({ type: 'loggedOut', payload: result.csrf_token });
    }
    return result;
  }, [withCsrf]);

  const addPackage = useCallback(
    (packageId, quantity = 1) => mutate((token) => api.addPackage(packageId, token, quantity)),
    [mutate],
  );
  const addGift = useCallback(
    (packageId, recipient, quantity = 1) => mutate((token) => api.addGiftPackage(packageId, recipient, token, quantity)),
    [mutate],
  );
  const setItemQuantity = useCallback(
    (packageId, quantity) => mutate((token) => api.setItemQuantity(packageId, quantity, token)),
    [mutate],
  );
  const removeItem = useCallback(
    (packageId) => mutate((token) => api.removeItem(packageId, token)),
    [mutate],
  );
  const clear = useCallback(() => mutate((token) => api.clearBasket(token)), [mutate]);
  const applyCoupon = useCallback(
    (code) => mutate((token) => api.applyCoupon(code, token)),
    [mutate],
  );
  const removeCoupon = useCallback(
    (code) => mutate((token) => api.removeCoupon(code, token)),
    [mutate],
  );
  const createCheckout = useCallback(
    () => withCsrf((token) => api.createCheckout({ csrfToken: token })),
    [withCsrf],
  );

  const value = useMemo(() => ({
    ...state,
    isEmpty: state.items.length === 0,
    coupon: state.coupons[0] ?? null,
    refresh,
    login,
    logout,
    addPackage,
    addGift,
    setItemQuantity,
    removeItem,
    clear,
    applyCoupon,
    removeCoupon,
    createCheckout,
    clearHandoff: () => dispatch({ type: 'handoffSeen' }),
  }), [
    state,
    refresh,
    login,
    logout,
    addPackage,
    addGift,
    setItemQuantity,
    removeItem,
    clear,
    applyCoupon,
    removeCoupon,
    createCheckout,
  ]);

  return <BasketContext.Provider value={value}>{children}</BasketContext.Provider>;
}

export function useBasket() {
  const context = useContext(BasketContext);
  if (!context) throw new Error('useBasket must be used inside a BasketProvider');
  return context;
}
